// medplum-tasks.js — ONLINE-PRIMARY Tasks worklist, the idiomatic Medplum way.
// The browser reads/writes FHIR Task DIRECTLY as the logged-in doctor (RLS-scoped
// by their own token via the shared getMedplum() session — see medplum-client.js),
// shaping client-side into the exact DTO Tasks.jsx already renders. This replaces
// the five Express call-sites in Tasks.jsx (list, assignees, create, patch, cancel)
// with FHIR-direct calls; the screen JSX only tries these first and falls through
// to its existing Express fetch on failure (the same guard idiom the queue/consult
// loaders use). The gated-resolve endpoints Tasks.jsx keeps (/tasks/:id/linked,
// /patients/:id/duplicates, /patients/merge, messaging, /scheduling/book) stay
// Express this wave — they are OUT OF SCOPE here.
//
// Row-level access: opd-doctor-rowlevel grants Task via `Task?owner=%profile` OR
// `Task?_compartment=%department` — so a search returns exactly the doctor's own +
// their department's Tasks with no app-side re-check (the redesign's safe-by-RLS
// invariant, AGENTS.md §2). On CREATE we stamp Task.owner to the doctor's own
// profile when the caller didn't pick an owner, so the write matches owner=%profile
// and can't 403 on a doctor whose department compartment isn't stamped yet.
import { getMedplum, invalidateCached } from './client-port.js';
import { searchResourcesBounded } from './medplum-bounded-search.js';
import { shapeTask, dedupeTasks } from '@healther/clinical-core/shape/task-row.js';

// Custom-extension / coding conventions — kept in lock-step with the server
// (tasks.mjs createTask/updateTask) and medplum-store.mjs's encodeHealtherExtension
// so a Task written here round-trips through the read shaper (task-row.js) and the
// server's own shapeTask identically.
const TASK_TYPE_SYSTEM = 'https://healther.io/opd/task-type';
const EXT_OPD_HEALTHER = 'http://healther.io/fhir/StructureDefinition/opd-healther';

// Human labels for the known task types — mirrors TASK_TYPES (tasks.mjs:64-73).
const TASK_TYPE_LABELS = {
  'review-report': 'Review result / report',
  'follow-up-call': 'Call patient / follow-up',
  'verify-id': 'Verify patient ID',
  'sign-off': 'Senior sign-off needed',
  'collect-sample': 'Collect lab sample',
  'chase-result': 'Chase pending result',
  refill: 'Prescription refill',
  general: 'General task',
};

const STATUSES = ['requested', 'ready', 'in-progress', 'on-hold', 'completed', 'cancelled'];
const PRIORITIES = ['routine', 'urgent', 'asap', 'stat'];
const OPEN_STATUSES = ['requested', 'ready', 'in-progress', 'on-hold']; // == server OPEN set
const PRIORITY_RANK = { stat: 0, asap: 1, urgent: 2, routine: 3 };
const PERFORMER_ROLES = [
  'doctor', 'senior-resident', 'junior-resident', 'consultant', 'hod',
  'nurse', 'pharmacy', 'reception', 'lab',
];

// 'any'/blank → no specific role (stored as no performerType). Mirrors
// normalizePerformerRole (tasks.mjs:56-60): free text is accepted too.
function normalizePerformerRole(role) {
  const r = String(role || '').trim().toLowerCase();
  if (!r || r === 'any') return null;
  return r;
}

// The logged-in doctor's own reference (Practitioner/<id> or the profile type). Used
// to stamp owner on create and to build the `mine` owner= filter.
function profileRef(medplum) {
  const p = medplum.getProfile();
  return p ? p.resourceType + '/' + p.id : null;
}

// ── List (GET /tasks?mine&open&status&performerRole → { tasks:[…] }) ───────────
// FHIR search mirroring listTasks()'s filter set (tasks.mjs:313-323):
//   mine     → owner=Practitioner/<me>       (RLS already scopes, this narrows to owned)
//   open     → status=requested,ready,in-progress,on-hold
//   status   → status=<that one value>
//   (neither open nor status) → all statuses
// performerRole is a CLIENT-SIDE filter (the server filters on _healther.performerRole,
// which we read via task-row.js's shaper). We sort client-side by priority rank then
// authoredOn because FHIR priority is not numerically _sort-ordered the way the app
// wants (same reason tasks.mjs:320-321 sorts in-process).
export async function loadTasksFromMedplum({ mine, open, status, performerRole } = {}) {
  const { medplum } = await getMedplum();

  const params = new URLSearchParams();
  if (mine) {
    const me = profileRef(medplum);
    if (me) params.set('owner', me);
  }
  if (status && STATUSES.includes(status)) params.set('status', status);
  else if (open) params.set('status', OPEN_STATUSES.join(','));

  const { resources: raw, truncated, fetchedCount, maxRows } = await searchResourcesBounded(medplum, 'Task', params.toString());
  let rows = dedupeTasks(raw);

  // Sort urgent-first, then oldest-first — identical to the server ordering so the
  // worklist looks the same whichever path served it. verify-id dup-suspects (urgent)
  // naturally float above thin-intake ones (routine) via PRIORITY_RANK.
  rows.sort((a, b) => (PRIORITY_RANK[a.priority] ?? 3) - (PRIORITY_RANK[b.priority] ?? 3)
    || String(a.authoredOn || '').localeCompare(String(b.authoredOn || '')));

  let tasks = rows.map(shapeTask);
  const role = normalizePerformerRole(performerRole);
  if (role) tasks = tasks.filter((t) => t.performerRole === role);
  return { tasks, truncated, fetchedCount, maxRows };
}

// ── One patient's OPEN tasks (the chart drawer's "Open tasks" strip) ──────────
// Task's standard `patient` search param resolves Task.for when it points at a
// Patient, so this is a plain compartment search narrowed to the OPEN statuses —
// the drawer only ever shows work that still needs doing. RLS remains the ONLY
// lock (Task?owner=%profile OR Task?_compartment=%department): an out-of-reach
// Task simply isn't in the result, so there is no app-side re-check here.
// Ordered soonest-due first (undated last), then urgency — the drawer shows a
// short list, so anything overdue has to be at the top of it.
export async function loadPatientTasksFromMedplum(patientId) {
  if (!patientId) throw new Error('loadPatientTasksFromMedplum requires a patientId');
  const { medplum } = await getMedplum();
  const params = new URLSearchParams();
  params.set('patient', 'Patient/' + patientId);
  params.set('status', OPEN_STATUSES.join(','));
  params.set('_count', '50');
  const raw = await medplum.searchResources('Task', params.toString());
  const rows = dedupeTasks(Array.isArray(raw) ? Array.from(raw) : []);
  // ￿ sorts after any ISO timestamp, so undated tasks fall to the bottom.
  const dueOf = (t) => ((t.restriction && t.restriction.period && t.restriction.period.end) || '￿');
  rows.sort((a, b) => dueOf(a).localeCompare(dueOf(b))
    || (PRIORITY_RANK[a.priority] ?? 3) - (PRIORITY_RANK[b.priority] ?? 3));
  return { tasks: rows.map(shapeTask) };
}

// ── Assignees (GET /tasks/assignees → { staff:[{id,name,role}] }) ─────────────
// Practitioner is readonly-granted in every policy (incl. opd-doctor-rowlevel), so a
// plain Practitioner search is the FHIR-direct replacement for the Express staff
// roster. `role` has NO stable home on a bare Practitioner (it lives on
// PractitionerRole, which the picker doesn't need) — the picker only uses `role` for
// search-matching + a display fallback and degrades gracefully when it's absent, so
// we emit null and leave a richer role-join to a later pass. COMMENT: role default.
export async function loadTaskAssignees() {
  const { medplum } = await getMedplum();
  const raw = await medplum.searchResources('Practitioner', '_count=200&_sort=name');
  const list = Array.isArray(raw) ? Array.from(raw) : [];
  const staff = list.map((p) => ({
    id: p.id,
    name: humanName(p) || 'Practitioner',
    role: null, // no FHIR home on Practitioner; picker tolerates null (search/display only)
  }));
  return { staff };
}

function humanName(p) {
  const n = (p && p.name && p.name[0]) || null;
  if (!n) return '';
  return n.text || [(n.given || []).join(' '), n.family].filter(Boolean).join(' ') || '';
}

// ── Create (POST /tasks → { task }) ───────────────────────────────────────────
// Builds the FHIR Task EXACTLY as createTask does (tasks.mjs:90-138), including the
// opd-healther extension so it round-trips through the read shaper unchanged.
// `body` is the NewTaskModal shape: { taskType, description, priority, patientId?,
// ownerId?, performerRole?, due? }. When the caller picked no owner we default owner
// to the doctor's OWN profile so the write matches Task?owner=%profile (avoids a 403
// where the department compartment isn't stamped) AND so it appears under "Mine".
export async function createTaskInMedplum(body = {}) {
  const { medplum } = await getMedplum();
  const {
    taskType = 'general', description = '', priority = 'routine',
    patientId = null, ownerId = null, performerRole = null, due = null,
    // OPTIONAL FHIR reference this task is ABOUT (Task.focus) — e.g. the
    // ServiceRequest a 'chase-result' ping chases (T2, createLabOrder). Omitted by
    // every hand-created task, which is why this stayed absent until now. It is a
    // plain { reference } object; anything else is ignored rather than written, so a
    // malformed caller can never mint a dangling reference.
    focus = null,
  } = body;

  const prio = PRIORITIES.includes(priority) ? priority : 'routine';
  const role = normalizePerformerRole(performerRole);
  const desc = String(description || TASK_TYPE_LABELS[taskType] || taskType).trim();
  if (!desc) throw new Error('description or a known taskType is required');

  // Task.for must be a REAL Medplum UUID — callers sometimes hold the disk-first
  // OPD slug (e.g. from the Express patient picker). Writing a slug would mint a
  // dangling Patient reference in the FHIR store, so reject it loudly here: the
  // call-site's guard idiom then falls through to the Express path, which is
  // slug-native. (Callers pass row.medplumId when the row has synced.)
  if (patientId && !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(patientId))) {
    throw new Error(`patientId "${patientId}" is not a Medplum UUID — refusing to write a dangling Task.for reference`);
  }

  // Default the owner to the logged-in doctor when unset (RLS + "Mine" — see header).
  const me = profileRef(medplum);
  const ownerReference = ownerId ? ('Practitioner/' + ownerId) : (me || null);
  const resolvedOwnerId = ownerId || (me && me.startsWith('Practitioner/') ? me.split('/')[1] : null);
  const ownerDisplay = ownerId ? undefined : (medplum.getProfile() && humanName(medplum.getProfile())) || undefined;

  const task = {
    resourceType: 'Task',
    status: ownerReference ? 'in-progress' : 'requested',
    intent: 'order',
    priority: prio,
    code: { text: taskType, coding: [{ system: TASK_TYPE_SYSTEM, code: taskType, display: TASK_TYPE_LABELS[taskType] || taskType }] },
    description: desc,
    authoredOn: new Date().toISOString(),
    ...(patientId ? { for: { reference: 'Patient/' + patientId } } : {}),
    ...(focus && typeof focus === 'object' && typeof focus.reference === 'string' && /^[A-Za-z]+\/[A-Za-z0-9-.]{1,64}$/.test(focus.reference)
      ? { focus: { reference: focus.reference } } : {}),
    ...(ownerReference ? { owner: { reference: ownerReference, display: ownerDisplay } } : {}),
    ...(role ? { performerType: [{ text: role }] } : {}),
    ...(due ? { restriction: { period: { end: new Date(due).toISOString() } } } : {}),
    requester: ownerDisplay ? { display: ownerDisplay } : undefined,
    // Mirror the internal _healther bag as a FHIR extension so the read shaper (and
    // the server's shapeTask) recover taskType/ownerId/performerRole identically.
    extension: [{
      url: EXT_OPD_HEALTHER,
      valueString: JSON.stringify({
        kind: 'opd-task', taskType,
        ownerId: resolvedOwnerId || null,
        performerRole: role || null,
        dept: null,
        createdBy: (me && me.startsWith('Practitioner/')) ? me.split('/')[1] : null,
      }),
    }],
  };

  const saved = await medplum.createResource(task);
  // Tasks.jsx calls loadTasks() the moment this resolves, and the my-tasks widget
  // re-reads the same Task search — both would be served the pre-create cache.
  await invalidateCached('Task');
  return { task: shapeTask(saved) };
}

// ── Patch (PATCH /tasks/:id → { task }) ───────────────────────────────────────
// Read-modify-write via updateResource, mapping the screen's patch verbs the SAME
// way updateTask does (tasks.mjs:164-221): status / ownerId(claim/reassign/clear) /
// priority / businessStatus / due(set/clear) / performerRole(set/clear) / note(append).
// A completed patch may carry { status:'completed', note } together (Detail.complete).
export async function patchTaskInMedplum(id, patch = {}) {
  const { medplum } = await getMedplum();
  const current = await medplum.readResource('Task', id);
  const next = { ...current };
  // Keep the encoded _healther in sync so ownerId/performerRole read back correctly.
  const healther = decodeHealtherFrom(current) || { kind: 'opd-task' };

  if (patch.status !== undefined) {
    if (!STATUSES.includes(patch.status)) throw new Error('bad status (allowed: ' + STATUSES.join(', ') + ')');
    next.status = patch.status;
  }
  if (patch.priority !== undefined) {
    if (!PRIORITIES.includes(patch.priority)) throw new Error('bad priority (allowed: ' + PRIORITIES.join(', ') + ')');
    next.priority = patch.priority;
  }
  // Claim/reassign/unassign. Setting an owner moves an unstarted task to in-progress
  // unless the caller passed an explicit status (mirrors updateTask:180-191).
  if (patch.ownerId !== undefined) {
    if (patch.ownerId === null) {
      delete next.owner; healther.ownerId = null;
    } else {
      const name = ownerDisplayFor(medplum, patch.ownerId);
      next.owner = { reference: 'Practitioner/' + patch.ownerId, display: name || undefined };
      healther.ownerId = patch.ownerId;
      if (patch.status === undefined && (next.status === 'requested' || next.status === 'ready')) {
        next.status = 'in-progress';
      }
    }
  }
  if (patch.businessStatus !== undefined) {
    if (patch.businessStatus) next.businessStatus = { text: String(patch.businessStatus) };
    else delete next.businessStatus;
  }
  // Due lives at Task.restriction.period.end. null/'' clears it; else parse-or-fail.
  if (patch.due !== undefined) {
    if (patch.due === null || patch.due === '') {
      delete next.restriction;
    } else {
      const d = new Date(patch.due);
      if (Number.isNaN(d.getTime())) throw new Error('bad due date (expected an ISO datetime)');
      next.restriction = { ...(next.restriction || {}), period: { ...((next.restriction && next.restriction.period) || {}), end: d.toISOString() } };
    }
  }
  // performerRole → Task.performerType[].text (+ _healther mirror). null/''/'any' clears.
  if (patch.performerRole !== undefined) {
    const role = normalizePerformerRole(patch.performerRole);
    if (role) { next.performerType = [{ text: role }]; healther.performerRole = role; }
    else { delete next.performerType; healther.performerRole = null; }
  }
  if (patch.note) {
    const text = String(patch.note).trim();
    if (text) {
      const authorName = (medplum.getProfile() && humanName(medplum.getProfile())) || undefined;
      next.note = [...(current.note || []), { text, time: new Date().toISOString(), authorString: authorName }];
    }
  }

  writeHealtherInto(next, healther);
  const saved = await medplum.updateResource(next);
  // Tasks.jsx applies an optimistic row patch and THEN re-reads; a cached search
  // here silently reverts the doctor's own change back to the pre-patch value.
  await invalidateCached('Task');
  return { task: shapeTask(saved) };
}

// ── Cancel (DELETE /tasks/:id → { task }) ─────────────────────────────────────
// SOFT-CANCEL — status='cancelled' + a businessStatus marker, mirroring deleteTask
// (tasks.mjs:241-262): a hard FHIR delete would erase the audit trail, so the server
// deliberately never hard-deletes here either. The UI drops the row immediately.
export async function cancelTaskInMedplum(id) {
  const { medplum } = await getMedplum();
  const current = await medplum.readResource('Task', id);
  const who = (medplum.getProfile() && humanName(medplum.getProfile())) || 'staff';
  const next = { ...current, status: 'cancelled', businessStatus: { text: 'Deleted by ' + who } };
  const saved = await medplum.updateResource(next);
  // The UI drops the row optimistically then reloads — without this the cancelled
  // task reappears from cache.
  await invalidateCached('Task');
  return { task: shapeTask(saved) };
}

// ── shared _healther extension helpers (encode/decode, same wire format) ──────
function decodeHealtherFrom(resource) {
  const ext = (resource && Array.isArray(resource.extension) ? resource.extension : [])
    .find((e) => e && e.url === EXT_OPD_HEALTHER && e.valueString);
  if (!ext) return null;
  try { return JSON.parse(ext.valueString); } catch (_) { return null; }
}

function writeHealtherInto(resource, healther) {
  const others = (resource.extension || []).filter((e) => !(e && e.url === EXT_OPD_HEALTHER));
  resource.extension = [...others, { url: EXT_OPD_HEALTHER, valueString: JSON.stringify(healther) }];
}

// Best-effort display name for an owner id, from the cached Practitioner if the
// client already has it; undefined otherwise (display is decorative — the id is what
// the picker matches on, so a missing name never breaks reassignment).
function ownerDisplayFor(medplum, id) {
  try {
    const cached = medplum.getCached && medplum.getCached('Practitioner', id);
    return cached ? humanName(cached) : undefined;
  } catch (_) { return undefined; }
}
